import React, { useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { createAPIClient } from '../../api';
import * as types from '../../api/types';
import { PrintLayout, usePrintData } from '../../components/PrintLayout';
import { dateParam, dateRangeError, isZeroAmount, reportDate, reportMoney } from '../reports/reportUi';
import { customerAddressLines, fetchAllStatementLines } from './printUi';

interface StatementPrintData {
  statement: types.CustomerStatementReport;
  customer: types.Customer;
  company: types.CompanySettings;
}

const cell = 'px-2 py-1.5 align-top';
const numericCell = `${cell} text-right whitespace-nowrap`;
const HEADINGS = ['Date', 'Type', 'Reference', 'Description', 'Charge', 'Credit', 'Balance'];
const NUMERIC_FROM = 4;

/**
 * /print/customers/:customerId/statement — every line of the statement for
 * the startDate/endDate in the query string (the same range as the screen).
 * All pages are fetched before anything renders; the running balance is the
 * server's.
 */
export function StatementPrintPage() {
  const { customerId = '' } = useParams<{ customerId: string }>();
  const [params] = useSearchParams();
  const api = useMemo(() => createAPIClient(), []);
  const startDate = dateParam(params.get('startDate'));
  const endDate = dateParam(params.get('endDate'));
  const state = usePrintData<StatementPrintData>(JSON.stringify({ customerId, startDate, endDate }), async () => {
    const rangeError = dateRangeError(startDate, endDate);
    if (rangeError) throw new Error(rangeError);
    // Statement first, so an unknown or other-tenant id shows the statement
    // endpoint's error, exactly as the on-screen statement page does.
    const [statement, company] = await Promise.all([
      fetchAllStatementLines((query) => api.getCustomerStatement(customerId, query), { startDate, endDate }),
      api.getCompanySettings(),
    ]);
    const customer = await api.getCustomer(customerId);
    return { statement, customer, company };
  }, 'Could not load the statement');
  const statement = state.data?.statement;
  const backQuery = new URLSearchParams({ ...(startDate ? { startDate } : {}), ...(endDate ? { endDate } : {}) }).toString();
  const title = statement ? `Statement - ${statement.customerName}` : 'Statement';

  return (
    <PrintLayout title={title} backTo={`/customers/${customerId}/statement${backQuery ? `?${backQuery}` : ''}`} backLabel="Back to statement" noun="statement" state={state} company={state.data?.company ?? null}>
      {state.data && statement && <StatementDocument statement={statement} customer={state.data.customer} />}
    </PrintLayout>
  );
}

function StatementDocument({ statement, customer }: { statement: types.CustomerStatementReport; customer: types.Customer }) {
  const addressLines = customerAddressLines(customer);
  const period = `${statement.startDate ? reportDate(statement.startDate) : 'Beginning'} – ${statement.endDate ? reportDate(statement.endDate) : 'Latest'}`;
  return (
    <>
      <section className="print-keep mb-8 flex flex-wrap items-start justify-between gap-6">
        <div>
          <h1 className="text-2xl font-bold tracking-wide">STATEMENT</h1>
          <h2 className="mt-4 mb-1 text-xs font-semibold uppercase tracking-wide text-gray-600">Customer</h2>
          <p className="font-semibold">{statement.customerName || customer.name}</p>
          {addressLines.map((line) => <p key={line}>{line}</p>)}
        </div>
        <dl className="grid grid-cols-[auto_auto] gap-x-4 gap-y-1">
          <dt className="text-gray-600">Period</dt><dd>{period}</dd>
          <dt className="text-gray-600">Opening balance</dt><dd className="text-right">{reportMoney(statement.openingBalance)}</dd>
          <dt className="font-semibold">Closing balance</dt><dd className="text-right font-semibold">{reportMoney(statement.closingBalance)}</dd>
        </dl>
      </section>

      <div className="overflow-x-auto print:overflow-visible">
        <table className="print-table w-full border-collapse text-sm" aria-label="Statement lines">
          <thead>
            <tr className="border-b-2 border-gray-800">
              {HEADINGS.map((heading, index) => <th key={heading} className={`${index >= NUMERIC_FROM ? 'text-right' : 'text-left'} px-2 py-2 font-semibold`}>{heading}</th>)}
            </tr>
          </thead>
          <tbody>
            <tr className="border-b border-gray-200">
              <td className={cell} colSpan={6}>Opening balance</td>
              <td className={numericCell}>{reportMoney(statement.openingBalance)}</td>
            </tr>
            {statement.lines.length === 0
              ? <tr><td colSpan={HEADINGS.length} className={`${cell} text-gray-600`}>No invoices or payments in this period.</td></tr>
              : statement.lines.map((line) => (
                <tr key={`${line.type}-${line.referenceId}`} className="border-b border-gray-200">
                  <td className={`${cell} whitespace-nowrap`}>{reportDate(line.date)}</td>
                  <td className={cell}>{line.type === 'PAYMENT' ? 'Payment' : line.invoiceType === 'FINANCE_CHARGE' ? 'Finance charge' : 'Invoice'}</td>
                  <td className={`${cell} whitespace-nowrap`}>{line.referenceNumber}</td>
                  <td className={cell}>{line.description}</td>
                  <td className={numericCell}>{isZeroAmount(line.charge) ? '' : reportMoney(line.charge)}</td>
                  <td className={numericCell}>{isZeroAmount(line.credit) ? '' : reportMoney(line.credit)}</td>
                  <td className={numericCell}>{reportMoney(line.runningBalance)}</td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      <dl className="print-keep mt-6 ml-auto max-w-xs" aria-label="Statement totals">
        <div className="flex justify-between gap-6 border-t-2 border-gray-800 pt-2 text-base font-bold"><dt>Closing balance</dt><dd>{reportMoney(statement.closingBalance)}</dd></div>
      </dl>
    </>
  );
}
